import React, { useCallback, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';

import { Alert, App, Button, Dropdown, Form, Pagination, Select, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';

import ModalActions from 'components/ModalActions';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import TopPage from 'v2/components/Shared/TopPage';
import Truncate from 'v2/components/Shared/TruncateText';
import WithBetaModules from 'v2/components/Shared/WithBetaModules';
import { useChangeStatusConnect } from 'v2/hooks/useChangeStatusConnect';
import { useListAutoresponders } from 'v2/hooks/useListAutoresponders';
import { useListMyConnects } from 'v2/hooks/useListMyConnects';

import { Icon } from '@eduzz/ui-antd-theme';

import NewConnect from '../components/NewConnect';
import { useConnectState } from '../contexts/ConnectStateContext';

const RDSTATION = 18,
  RDSTATIONEW = 44,
  GDIGITAL = 43,
  GDIGITALNEW = 46;

const DEV_HUB_AUTH_URL =
  'https://ajuda.eduzz.com/hc/pt-br/articles/30130496111771-Como-funciona-o-processo-de-autentica%C3%A7%C3%A3o-com-a-API-Developer-Hub-da-Eduzz';
const ZAPIER_HELP_URL = 'https://ajuda.eduzz.com/hc/pt-br/articles/11780815400859-Como-integrar-utilizando-o-Zapier';

const MyConnects: React.FC = () => {
  const { message } = App.useApp();
  const { currentConnect, openNewConnectModal, setCurrentConnect } = useConnectState();
  const [paginationParams, setPaginationParams] = useState({ per_page: 25, current_page: 1 });
  const [openModalDisable, setOpenModalDisable] = useState(false);
  const [filter, setFilter] = useState<number | ''>('');

  const history = useHistory();

  const { myConnects: connects, pagination, isLoading: isFetching, refetch } = useListMyConnects(paginationParams);
  const { autoresponders } = useListAutoresponders();
  const { changeStatusConnect } = useChangeStatusConnect();

  const openModal = useCallback(() => {
    openNewConnectModal();
  }, [openNewConnectModal]);

  const openModalDisableConnect = useCallback(
    (connectItem: any) => {
      setOpenModalDisable(true);
      setCurrentConnect(connectItem);
    },
    [setCurrentConnect]
  );

  const onCancelModal = useCallback(() => {
    setOpenModalDisable(false);
  }, []);

  const changeConnectStatus = useCallback(
    async (status: boolean) => {
      try {
        await changeStatusConnect({ connectId: currentConnect.id, active: status });
        setOpenModalDisable(false);
        message.success(`Connect ${status ? 'ativado' : 'desativado'} com sucesso!`);
        refetch();
      } catch (error) {
        message.error(typeof error === 'string' ? error : 'Não foi possível alterar o status do connect.');
      }
    },
    [currentConnect, changeStatusConnect, message, refetch]
  );

  const providers = Array.isArray(autoresponders) ? autoresponders : [];
  const listConnects = Array.isArray(connects) ? connects : [];

  const filteredConnects = listConnects.filter((c: any) => {
    if (!filter) return true;
    if (filter === RDSTATION && (c.autoresponder_id === RDSTATION || c.autoresponder_id === RDSTATIONEW)) return true;
    if (filter === GDIGITAL && (c.autoresponder_id === GDIGITAL || c.autoresponder_id === GDIGITALNEW)) return true;
    return c.autoresponder_id === filter;
  });

  const isAlertProviderDeprecatedEnabled = new Date() < new Date('2025-10-30');

  const getMenuItems = (connectItem: any) => {
    if (connectItem?.migrated) {
      return [
        {
          key: 'edit-new',
          label: 'Editar no Novo Connect',
          onClick: () => history.push('/producer/new-connect')
        }
      ];
    }

    return [
      {
        key: 'toggle-status',
        label: connectItem?.active ? 'Desativar' : 'Ativar',
        onClick: () => openModalDisableConnect(connectItem)
      },
      {
        key: 'view-actions',
        label: 'Ver Ações',
        onClick: () => history.push(`/producer/connect/${connectItem.id}/actions`)
      }
    ];
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Status',
      dataIndex: 'active',
      key: 'status',
      render: (active: boolean) => <Tag color={active ? 'success' : 'error'}>{active ? 'Ativo' : 'Inativo'}</Tag>
    },
    {
      title: 'Plataforma',
      dataIndex: 'image',
      key: 'platform',
      render: (_: unknown, record: any) => (
        <img
          src={record.image}
          alt={record.name || 'Plataforma'}
          title={record.name || 'Desconhecido'}
          className='h-9 max-w-20 object-contain'
        />
      )
    },
    {
      title: 'Descrição',
      dataIndex: 'description',
      key: 'description'
    },
    {
      title: 'Dados de configuração',
      key: 'config',
      render: (_: unknown, record: any) => (
        <div className='flex flex-col'>
          <Typography.Text strong>{record?.parameters[0]?.label ?? '-'}</Typography.Text>
          <Truncate count={100} final='...'>
            {record?.parameters[0]?.value}
          </Truncate>
        </div>
      )
    },
    {
      title: '',
      key: 'actions',
      width: 56,
      align: 'right',
      render: (_: unknown, record: any) => (
        <Dropdown menu={{ items: getMenuItems(record) }} trigger={['click']}>
          <Button type='text' shape='circle' aria-label='Ações do connect' icon={<Icon.Ellipsis size={16} />} />
        </Dropdown>
      )
    }
  ];

  return (
    <>
      <TopPage
        title='Connect'
        action={
          <PermissionHide action='myeduzz:connect:criar'>
            <Button type='primary' icon={<Icon.Plus size={16} />} onClick={openModal}>
              Nova integração
            </Button>
          </PermissionHide>
        }
      />

      {isAlertProviderDeprecatedEnabled && (
        <Alert
          className='mb-4'
          type='warning'
          showIcon
          message='Atenção: algumas integrações serão descontinuadas em 30/10'
          description={
            <>
              A partir de 30/10, os provedores ConvertKit, Hotzapp, GDigital, GDigitalNew, InfusionSoft, Aweber,
              MailRelay e Pipz deixarão de estar disponíveis.
              <br />
              <br />
              Recomendamos migrar suas automações para a{' '}
              <Typography.Link href={DEV_HUB_AUTH_URL} target='_blank' rel='noreferrer'>
                Developer Hub
              </Typography.Link>{' '}
              ou{' '}
              <Typography.Link href={ZAPIER_HELP_URL} target='_blank' rel='noreferrer'>
                Zapier
              </Typography.Link>
              .
            </>
          }
        />
      )}

      <PermissionHide action='owner'>
        <WithBetaModules>
          {(betaModules: string[]) => {
            if (betaModules?.includes('new_connect')) {
              return (
                <Alert
                  className='mb-4'
                  type='info'
                  showIcon
                  message={
                    <>
                      <Typography.Text strong>Uma nova forma de fazer integrações está chegando! </Typography.Text>
                      <Typography.Text>
                        Estamos criando uma nova versão do Connect Eduzz e já migramos alguns dos seus provedores para a
                        nova versão. <Link to='/producer/new-connect'>Bora conferir?</Link>
                      </Typography.Text>
                    </>
                  }
                />
              );
            }

            return null;
          }}
        </WithBetaModules>
      </PermissionHide>

      <div className='mt-4 flex flex-col gap-4'>
        <div className='flex justify-end'>
          <Form layout='vertical' className='w-full md:w-1/4'>
            <Form.Item label='Plataforma' className='mb-0'>
              <Select
                className='w-full'
                value={filter}
                onChange={value => setFilter(value)}
                options={[
                  { value: '', label: 'Todas' },
                  ...providers
                    .filter((a: any) => a.id !== RDSTATIONEW && a.id !== GDIGITALNEW)
                    .map((option: any) => ({ value: option.id, label: option.name }))
                ]}
              />
            </Form.Item>
          </Form>
        </div>

        <Table
          columns={columns}
          dataSource={filteredConnects}
          rowKey={(record: any) => `connect-${record.id}`}
          loading={isFetching}
          pagination={false}
          scroll={{ x: 'max-content' }}
          locale={{ emptyText: 'Nenhuma integração encontrada' }}
        />

        <div className='flex justify-end'>
          <Pagination
            total={pagination?.total_items || 0}
            current={pagination?.current_page ?? 1}
            pageSize={pagination?.per_page ?? 25}
            pageSizeOptions={[10, 25, 50]}
            showSizeChanger
            onChange={(page, pageSize) =>
              setPaginationParams(prev => ({
                ...prev,
                current_page: page,
                per_page: pageSize ?? prev.per_page
              }))
            }
          />
        </div>

        <NewConnect />
        <ModalActions
          open={openModalDisable}
          onClose={onCancelModal}
          onOk={() => changeConnectStatus(!currentConnect.active)}
          title='Atenção'
          buttonOk='Confirmar'
        >
          <Typography.Paragraph className='mb-0'>
            Você deseja realmente {currentConnect && currentConnect.active ? 'desativar' : 'ativar'} o connect{' '}
            <Typography.Text strong>{currentConnect && currentConnect.name}</Typography.Text> -{' '}
            {currentConnect?.description}?
          </Typography.Paragraph>
        </ModalActions>
      </div>
    </>
  );
};

export default MyConnects;
